import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";

const initialState = {
  loading: false,
  error: null,
  lcaAndBwv: [],
  finalLcaAndBwv: [],
  bizz: [],
  loadingBizz: false,
  errorBizz: null,
  totalRecords: 0,
  onlineView: JSON.parse(localStorage.getItem("onlineView")) || false,
  endorsements: [],
  loadingEndorsements: false,
  errorEndorsements: null,
  selectedPostIdForEndorsements: null,
};

export const fetchEndorsements = createAsyncThunk(
  "LcaAndBwv/fetchEndorsements",
  async (postId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `services/UserEndorsementDetails/${postId}`
      );
      return response.data.data || [];  
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch endorsements"
      );
    }
  }
);

export const fetchLcaAndBwv = createAsyncThunk(
  "LcaAndBwv/fetchLcaAndBwv",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosClient.get(`services/getAllPosts`, {
        params,
      });
      dispatch(setTotalRecords(response.data.TotalRecords || 0));
      return response.data.Data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch data"
      );
    }
  }
);

export const fetchBizz = createAsyncThunk(
  "LcaAndBwv/getAllBizzValue",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`need/getAllBizzValue`, {
        params,
      });
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch data"
      );
    }
  }
);

const LcaAndBwvSlice = createSlice({
  name: "LcaAndBwv",
  initialState,
  reducers: {
    clearLcaAndBwv: (state) => {
      state.lcaAndBwv = [];
      state.finalLcaAndBwv = [];
      state.error = null;
    },
    setTotalRecords: (state, action) => {
      state.totalRecords = action.payload;
    },
    setLcaAndBwv: (state, action) => {
      state.lcaAndBwv = action.payload;
    },
    setFinalLcaAndBwv: (state, action) => {
      state.finalLcaAndBwv = action.payload;
    },
    setOnlineView: (state, action) => {
      state.onlineView = action.payload;
      localStorage.setItem("onlineView", JSON.stringify(action.payload));
    },
    clearEndorsements: (state) => {
      state.endorsements = [];
      state.selectedPostIdForEndorsements = null;
    },
  },
  extraReducers: (builder) => {
    // fetchLcaAndBwv
    builder
      .addCase(fetchLcaAndBwv.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLcaAndBwv.fulfilled, (state, action) => {
        state.loading = false;
        state.lcaAndBwv = action.payload;
        state.finalLcaAndBwv = action.payload;
      })
      .addCase(fetchLcaAndBwv.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      });

    // fetchBizz
    builder
      .addCase(fetchBizz.pending, (state) => {
        state.loadingBizz = true;
        state.errorBizz = null;
      })
      .addCase(fetchBizz.fulfilled, (state, action) => {
        state.loadingBizz = false;
        state.bizz = action.payload;
      })
      .addCase(fetchBizz.rejected, (state, action) => {
        state.loadingBizz = false;
        state.errorBizz = action.payload || "Something went wrong";
      });

    // fetchEndorsements
    builder
      .addCase(fetchEndorsements.pending, (state) => {
        state.loadingEndorsements = true;
        state.errorEndorsements = null;
        state.endorsements = []; // Clear previous endorsements
      })
      .addCase(fetchEndorsements.fulfilled, (state, action) => {
        state.loadingEndorsements = false;
        state.endorsements = action.payload;
      })
      .addCase(fetchEndorsements.rejected, (state, action) => {
        state.loadingEndorsements = false;
        state.errorEndorsements =
          action.payload || "Failed to load endorsements";
        state.endorsements = [];
      });
  },
});

export const {
  clearLcaAndBwv,
  setTotalRecords,
  setLcaAndBwv,
  setFinalLcaAndBwv,
  setOnlineView,
  clearEndorsements,
} = LcaAndBwvSlice.actions;

export default LcaAndBwvSlice.reducer;
